import { Injectable } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { Logger } from './helper/app-error-logger';

import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial/ngx';
import { AppConfig } from './helper/app.config';
import { MyApp } from './helper/app';
import { MobileWaage } from './waagen/waage';
import { WelvaartsWaage } from './waagen/welvaarts-waage';
import { GeraetStatusTyp, BluetoothDevice, AuftragEx, AuftragspositionEx, WaageKonfiguration, WaageZuordnungTyp } from './model/model';
import { AppStorage } from './helper/app-storage';
import { ToastController } from '@ionic/angular';
import { Utils } from './helper/utils';
import { SystemService } from './system.service';
import { Wiegedaten, TextdateiTyp, Textdatei, Waage, WiegenResult, GpsPosition, WiegenRequest } from './model/swagger-model';


import { RemoteService } from './remote.service';
import { UiHelper } from './helper/ui-helper';
import { SywatecWaage } from './waagen/sywatec-waage';
import { BoscheWaage } from './waagen/bosche-waage';
import { PfreundtWaage as PfreundtWaage } from './waagen/pfreundt-waage';
import { getWaageModusInfo } from './waagen/waage-modus.helper';

const log = new Logger('WaageService');

@Injectable({
    providedIn: 'root'
})
export class WaageService {
    static instance: WaageService;

    anzahlWiegungen = new BehaviorSubject<number>(0);
    waageStatus = new BehaviorSubject<GeraetStatusTyp>(GeraetStatusTyp.Unbekannt);

    private updateIntervalId: any;

    private waagen: MobileWaage[] = [];

    public wiegedaten = new BehaviorSubject<Wiegedaten[]>([]);

    private waageSubscriptions: { [adresse: string]: Subscription[]; } = {};

    private simulationLetzteAlibiNummer = 0;

    /**
     * Wird auf TRUE gesetzt, solange die App selbst auf das Ergebnis einer von ihr angeforderten Wiegung wartet.
     * Diese Wiegung wird direkt der Auftragsposition zugeordnet und darf deshalb nicht zusätzlich
     * in die Liste der offenen Wiegungen aufgenommen werden.
     */
    wiegungWirdVonAppErwartet = false;

    constructor(
        private bluetoothSerial: BluetoothSerial,
        private systemService: SystemService,
        private remoteService: RemoteService,
        private toastController: ToastController) {

        WaageService.instance = this;
    }

    async init(): Promise<void> {
        log?.debug('init');

        await MyApp.ready();

        const dbWiegedaten: Wiegedaten[] = await AppStorage.current.get('wiegedaten', true, false);

        if (dbWiegedaten) {
            this.wiegedaten.next(dbWiegedaten);
            this.anzahlWiegungen.next(dbWiegedaten.length)
        }

        MyApp.current.configRefreshed.subscribe(() => this.initWaage());

        setTimeout(() => {
            this.initWaage();
        }, 10 * 1000);
    }

    /**
     * Wird aufgerufen wenn Einstellungen geändert wurden
     */
    async restart(): Promise<void> {
        log?.info('restart');
        await this.initWaage();
    }

    private async initWaage() {
        log?.debug('initWaage');

        if (this.updateIntervalId) {
            clearInterval(this.updateIntervalId);
            this.updateIntervalId = null;
        }

        if (!MyApp.current.waageVerfuegbar.getValue()) {
            this.waageStatus.next(GeraetStatusTyp.NichtVerbunden);
            return;
        }

        const waageKonfigurationen = this.getWaageKonfigurationen();

        // Schließe Waagen-Implementierungen von nicht mehr vorhandennen Waagen
        for (const waage of [...this.waagen]) {
            let disposeErforderlich = false;

            const waageKonfig = waageKonfigurationen.find(p => p.macAdresse == waage.adresse);

            if (!waageKonfig) {
                // Waage wurde gelöscht
                disposeErforderlich = true;
            } else {
                if (waageKonfig.typ && waageKonfig.typ !== waage.getTyp()) {
                    // Typ hat sich geändert
                    disposeErforderlich = true;
                }
            }

            if (disposeErforderlich) {
                this.disposeWaage(waage);
            }
        }

        // Alle nicht mehr aktiven Waagen zuerst schließen
        for (const waage of [...this.waagen]) {
            if (!waage.konfiguration.aktiv) {
                waage.setActive(false);
            }
        }

        // Initialisiere neue Waagen
        for (const waageConfig of waageKonfigurationen) {
            let waage = this.waagen.find(p => p.adresse == waageConfig.macAdresse);

            if (!waage) {
                waage = this.erstelleWaage(waageConfig);

                if (waage) {
                    const subscriptions: Subscription[] = [];

                    subscriptions.push(waage.error.subscribe(e => this.onError(waage, e)));
                    subscriptions.push(waage.status.subscribe(s => this.onStatusChanged(waage, s)));
                    // subscriptions.push(waage.disconnected.subscribe(w => this.onStatusChanged(waage, GeraetStatusTyp.NichtVerbunden)));
                    // subscriptions.push(waage.datenEmpfangen.subscribe(w => this.onStatusChanged(waage, GeraetStatusTyp.Verbunden)));
                    subscriptions.push(waage.wiegedatenEmpfangen.subscribe(w => this.onWiegedatenEmpfangen(waage, w)));

                    this.waagen.push(waage);
                }
            }

            if (waage) {
                waage.setActive(waageConfig.aktiv);
            }
        }
    }

    getWaageKonfigurationen(): WaageKonfiguration[] {
        const appConfig = AppConfig.current;

        if (!appConfig.gekoppelteWaagen) {
            appConfig.gekoppelteWaagen = [];
        }

        for (const cfg of appConfig.gekoppelteWaagen) {
            if (!cfg.zuordnung) {
                cfg.zuordnung = 'Fahrzeug';
            }
        }

        return appConfig.gekoppelteWaagen;
    }

    getAktiveWaageKonfigurationen(): WaageKonfiguration[] {
        return this.getWaageKonfigurationen().filter(p => p.aktiv);
    }

    private erstelleWaage(konfiguration: WaageKonfiguration): MobileWaage {
        log?.debug('erstelleWaage: ', konfiguration);

        let waage: MobileWaage;

        if (konfiguration.typ === 'welvaarts') {
            waage = new WelvaartsWaage(this.bluetoothSerial);
        } else if (konfiguration.typ === 'sywatec') {
            waage = new SywatecWaage(this.bluetoothSerial);
        } else if (konfiguration.typ === 'bosche') {
            waage = new BoscheWaage(this.bluetoothSerial);
        } else if (konfiguration.typ === 'pfreundt') {
            waage = new PfreundtWaage(this.bluetoothSerial);
        } else {
            throw new Error('Unbekannter Waagen-Typ: ' + konfiguration.typ);
        }

        waage.adresse = konfiguration.macAdresse;
        waage.konfiguration = konfiguration;

        return waage;
    }

    private async disposeWaage(waage: MobileWaage) {
        log?.info('disposeWaage', waage);

        try {
            const subscriptions = this.waageSubscriptions[waage.adresse];

            if (subscriptions) {
                subscriptions.forEach(p => p.unsubscribe());
                delete this.waageSubscriptions[waage.adresse];
            }
        } catch (err) {
            log.error('disposeWaage 1', err);
        }

        try {
            waage.dispose();
        } catch (err) {
            log.error('disposeWaage 2', err);
        }

        const index = this.waagen.indexOf(waage);

        if (index >= 0) {
            this.waagen.splice(index, 1);
        }
    }

    // private async close() {
    //     log?.info('Close');

    //     if (!App.isCordovaAvailable()) {
    //         return;
    //     }

    //     try {
    //         if (this.waage) {
    //             const w = this.waage;
    //             this.waage = null;
    //             await w.dispose();
    //         }
    //     } catch (err) {
    //         log.error('Close', err);
    //     }

    //     this.waageSubscriptions.forEach(p => p.unsubscribe());
    //     this.waageSubscriptions = [];
    // }

    async sucheBluetoothGeraete(): Promise<BluetoothDevice[]> {
        log?.debug('sucheBluetoothGeraete');

        const resultList: BluetoothDevice[] = [];

        resultList.push(... await this.getGekoppelteBluetoothGeraete());

        if (MyApp.isCordovaAvailable()) {
            try {
                const unpairedDevices: BluetoothDevice[] = await this.bluetoothSerial.discoverUnpaired();

                log?.info('Unpaired Devices: ' + JSON.stringify(unpairedDevices));

                for (const device of unpairedDevices) {
                    if (!device.name) {
                        device.name = device.address;
                    }

                    if (!resultList.find(p => p.address == device.address)) {
                        resultList.push(device);
                    }
                }
            } catch (err) {
                log.error('sucheBluetoothGeraete: ' + Utils.getErrorMessage(err), err);
            }
        }

        log?.debug('sucheBluetoothGeraete result: ' + JSON.stringify(resultList));

        return resultList;
    }

    async getGekoppelteBluetoothGeraete(): Promise<BluetoothDevice[]> {
        log?.debug('getGekoppelteBluetoothGeraete');

        if (!MyApp.isCordovaAvailable()) {
            return [{
                id: '111111',
                address: '111111',
                name: ''
            }, {
                id: '222222',
                address: '222222',
                name: ''
            }, {
                id: '333333',
                address: '333333',
                name: ''
            }];
        }

        log?.debug('Aktiviere Bluetooth...');
        const success = await this.bluetoothSerial.enable();

        if (!success) {
            log.warn('Bluetooth konnte nicht aktiviert werden');
            return null;
        }

        const list = await this.bluetoothSerial.list();

        log?.info('Bluetooth Geräte: ' + JSON.stringify(list));

        return list;
    }

    async onStatusChanged(waage: MobileWaage, status: GeraetStatusTyp) {
        log?.debug('onStatusChanged: ' + status);

        let gesamtStatus = GeraetStatusTyp.NichtVerbunden;

        switch (status) {
            case GeraetStatusTyp.Verbunden:
                gesamtStatus = GeraetStatusTyp.Verbunden;
                break;

            default:
                for (const w of this.waagen) {
                    if (w.status.getValue() == GeraetStatusTyp.Verbunden) {
                        gesamtStatus = GeraetStatusTyp.Verbunden;
                        break;
                    }
                }
        }

        if (this.waageStatus.getValue() != gesamtStatus) {
            this.waageStatus.next(gesamtStatus);
        }
    }

    async onError(waage: MobileWaage, error: string) {
        log.warn(`Fehler von Waage '${waage.konfiguration.name}': ${error}`);

        this.waageStatus.next(GeraetStatusTyp.Fehler);

        // const toast = await this.toastController.create({
        //     message: `Fehler von Waage '${waage.konfiguration.name}': ${error}`,
        //     duration: 5000,
        //     color: 'warning'
        // });

        // toast.present();
    }

    async loescheAlleWiegedaten() {
        log?.debug('loescheAlleWiegedaten');

        const wiegedaten = this.wiegedaten.getValue();

        const toast = await this.toastController.create({
            message: 'Wiegungen wurden gelöscht',
            duration: 1500,
            color: 'medium'
        });

        toast.present();

        this.setzeWiegedatenListe([]);

        const textdatei: Textdatei = {
            typ: TextdateiTyp.GeloeschteWiegungen,
            datum: Utils.nowIsoDateString(),
            geraeteNummer: AppConfig.current.geraeteNummer,
            GeloeschteWiegungen: {
                Personalnummer: MyApp.current.getPersonalnummer(),
                FahrzeugKey: MyApp.current.getFahrzeugKey(),
                Wiegungen: [...wiegedaten]
            }
        };

        await this.systemService.sendeTextdatei(textdatei, true);
    }

    async setzeWiegedatenListe(wiegedaten: Wiegedaten[]) {
        log?.debug('setzeWiegedatenListe: ' + wiegedaten.length);

        if (!getWaageModusInfo().zuordnen) {
            // Zuordnen ist nicht erlaubt (Modus 'wiegen'). Die Wiegung erfolgt immer aus der App heraus und wird
            // direkt der Auftragsposition zugeordnet. Die Liste wird deshalb nicht benötigt und bleibt immer leer.
            wiegedaten = [];
        }

        await AppStorage.current.set('wiegedaten', wiegedaten);

        this.wiegedaten.next(wiegedaten);
        this.anzahlWiegungen.next(wiegedaten.length);
    }

    async onWiegedatenEmpfangen(waage: MobileWaage, wiegedaten: Wiegedaten) {
        log?.debug('onWiegedatenEmpfangen', wiegedaten);

        if (this.wiegungWirdVonAppErwartet) {
            // Die App hat diese Wiegung selbst angefordert und ordnet sie direkt der Auftragsposition zu.
            // Deshalb hier nicht zusätzlich in die Liste aufnehmen.
            return;
        }

        if (!getWaageModusInfo().zuordnen) {
            // Im Modus 'wiegen' werden die Daten immer direkt einer Auftragsposition zugeordnet.
            // Daher hier nicht in die Liste aufnehmen.
            return;
        }

        this.waageStatus.next(GeraetStatusTyp.Verbunden);

        const toast = await this.toastController.create({
            message: 'Wiegung empfangen: ' + wiegedaten.Netto + ' kg',
            duration: 1500,
            color: 'medium'
        });

        toast.present();

        if (AppConfig.current.einstellungen.WaageSoundBeiWiegungAktiv == 'ja') {
            this.systemService.beepFuerNeueNachricht();
        }

        const list = this.wiegedaten.getValue();
        list.push(wiegedaten);

        this.setzeWiegedatenListe(list);
    }

    getWaageKonfigurationByMacAdresse(macAdresse: string): WaageKonfiguration {
        const appConfig = AppConfig.current;

        return appConfig.gekoppelteWaagen.find(p => p.macAdresse && p.macAdresse.toLowerCase() == macAdresse.toLowerCase());
    }

    getWaage(adresse: string): MobileWaage {
        log?.debug('getWaage: ' + adresse, this.waagen);
        return this.waagen.find(p => p.adresse == adresse);
    }

    getAktiveWaage(zuordnung: WaageZuordnungTyp = 'Fahrzeug'): MobileWaage {
        const aktiveWaagen = this.waagen.filter(p => p.konfiguration?.aktiv);

        for (const waage of aktiveWaagen) {
            // Daten korrigieren. Sollte eigentlich nicht mehr vorkommen
            if (!waage.konfiguration.zuordnung) {
                waage.konfiguration.zuordnung = 'Fahrzeug';
            }
        }

        return aktiveWaagen.find(p => p.konfiguration?.zuordnung === zuordnung);
    }

    async getRemoteWaagenInReichweite(): Promise<Waage[]> {
        if (!MyApp.isInternetAvailable()) {
            return [];
        }

        let position: GpsPosition;

        if (AppConfig.current.einstellungen.StationaereWaagenGpsPruefen === 'ja') {
            if (AppConfig.current.einstellungen.WiegenMitExakterGpsPosition == 'ja') {
                // Versuche eine bessere Position zu ermitteln
                position = await this.systemService.getExakteGpsPosition('wiegen Remote', true);
            } else {
                position = await this.systemService.getAktuelleGpsPosition('getRemoteWaagenInReichweite', 30);
            }

            if (!position) {
                log.warn('Aktuelle Position konnte nicht bestimmt werden. Verwende letzte bekannte Position.');

                position = this.systemService.letzteGpsPosition;
            };
        }

        if (!position) {
            log.warn('Keine GPS-Position bekannt');

            position = {
                Longitude: 0,
                Latitude: 0
            }
        }

        try {
            let waagen = await this.remoteService.getWaagen(position.Latitude, position.Longitude);

            if (waagen) {
                if (AppConfig.current.UnterMandant) {
                    // Nur Waagen zum passenden Unter-Mandant anzeigen
                    waagen = waagen.filter(p => !p.UnterMandant || p.UnterMandant == AppConfig.current.UnterMandant);
                }

                return waagen;
            }
        } catch (err) {
            log.error(Utils.getErrorMessage(err));
        }

        return [];
    }

    async wiegenRemote(request: WiegenRequest): Promise<WiegenResult> {
        log?.debug('wiegenRemote: ' + request.WaageKey);

        let position = this.systemService.letzteGpsPosition;

        if (AppConfig.current.einstellungen.WiegenMitExakterGpsPosition == 'ja') {
            if (!position || position.Accuracy > MyApp.current.PositionenExaktMeter) {
                log?.info('wiegenRemote: Position ist zu ungenau. Erneut nach einer exakten GPS-Position suchen');

                // Versuche eine bessere Position zu ermitteln
                position = await this.systemService.getExakteGpsPosition('wiegen Remote', true);

                if (!position) {
                    return {
                        Success: false,
                        Message: 'GPS-Position konnte nicht bestimmt werden.'
                    }
                }
            }
        }

        try {
            request.Position = position;
            request.GeraeteNummer = AppConfig.current.geraeteNummer;
            request.FahrzeugKey = MyApp.current.getFahrzeugKey();
            request.FehrzeugKennzeichen = MyApp.current.getFahrzeugKennzeichen();

            const tour = MyApp.current.aktuelleTour.getValue();

            if (tour) {
                request.TourBezeichnung = tour.TourBezeichnung;
                request.TourKey = tour.TourKey;
            }

            const fahrer = MyApp.current.fahrer.getValue();

            if (fahrer) {
                request.PersonalKey = fahrer.Key;
                request.Personalnummer = fahrer.personalnummer;
                request.Vorname = fahrer.vorname;
                request.Nachname = fahrer.nachname;
            }

            const result = await this.remoteService.wiegen(request);

            if (result) {
                return result;
            } else {
                return {
                    Success: false,
                    Message: 'Kommunikationsfehler'
                }
            }
        } catch (err) {
            log.error(Utils.getErrorMessage(err));

            return {
                Success: false,
                Message: Utils.getErrorMessage(err)
            }
        }
    }

    /**
     * Gibt die letzte bekannte / gelesene Alibi-Nummer zurück.
     * Wird für den Energor-Ablauf mit BOSCHE-Waagen benötigt.
     */
    async getLetzteGeleseneAlibiNr(): Promise<number> {
        let letzteBekannteAlibiNr = await AppStorage.current.get('letzteAlibiNr', true, true);

        if (letzteBekannteAlibiNr == null || typeof letzteBekannteAlibiNr !== 'number') {
            letzteBekannteAlibiNr = 0;
        }

        return letzteBekannteAlibiNr;
    }

    async speichereLetzteGeleseneAlibiNr(nr: number) {
        log?.debug('speichereLetzteGeleseneAlibiNr: ' + nr);

        await AppStorage.current.set('letzteAlibiNr', nr);
    }

    async leseLetzteAlibiNummer(): Promise<number> {
        log?.debug('leseLetzteAlibiNummer');

        try {
            if (!MyApp.isCordovaAvailable()) {
                // Simulation: Nur für die Entwicklung
                return this.leseLetzteAlibiNummerSimulation();
            }

            const waage = this.getAktiveWaage();

            if (!waage) {
                UiHelper.showAlert('Keine aktive Waage gefunden', 'Fehler');
                return -1;
            }

            if (typeof waage.leseLetzteAlibiNummer !== 'function') {
                UiHelper.showAlert('Die aktive Waage unterstützt das Lesen der letzten Alibi-Nummer nicht', 'Fehler');
                return -1;
            }

            return await waage.leseLetzteAlibiNummer();
        } catch (err) {
            log.error('leseLetzteAlibiNummer: ' + Utils.getErrorMessage(err), err);
        } finally {
            log?.debug('leseLetzteAlibiNummer DONE');
        }

        return 0;
    }

    async disconnect(): Promise<void> {
        log?.debug('disconnect');

        try {
            if (!MyApp.isCordovaAvailable()) {
                return;
            }

            const waage = this.getAktiveWaage();

            if (!waage) {
                return;
            }

            await waage.close();
        } catch (err) {
            log.error('disconnect: ' + Utils.getErrorMessage(err), err);
        } finally {
            log?.debug('disconnect DONE');
        }
    }

    async leseLetzteAlibiNummerSimulation(): Promise<number> {
        await Utils.delay(250);

        if (this.simulationLetzteAlibiNummer <= 0) {
            for (const wiegedaten of this.wiegedaten.getValue()) {
                if (wiegedaten.Wiegenummer > this.simulationLetzteAlibiNummer) {
                    this.simulationLetzteAlibiNummer = wiegedaten.Wiegenummer;
                }
            }
        }

        const d = Math.round(Math.random() * 3);

        this.simulationLetzteAlibiNummer += d;
        return this.simulationLetzteAlibiNummer;
    }

}
